import React from 'react';
import { Info } from 'lucide-react';
import type { RiskInsightsSiteRow } from 'src/app/portfolio/state/api/riskInsightsSitesQuery';
import type { RiskScoreDirection } from 'src/shared/components/risk-score-cell/riskScoreColour';
import RiskScoreCell from 'src/shared/components/risk-score-cell/RiskScoreCell';
import QuestionButtonHelp from 'src/shared/components/question-button-help/QuestionButtonHelp';
import SiteCell from '../../site-list/SiteCell';

type ScoreKey = 'physicalNatureRisk' | 'operationalDependencies' | 'impactsOnNature' | 'stateOfNature';

export type Top5SitesListProps = {
    title: string;
    sites: RiskInsightsSiteRow[];
    scoreKey: ScoreKey;
    direction: RiskScoreDirection;
    scoreType: 'Risk' | 'Condition';
    isLoading: boolean;
    slug?: string;
    customerId?: string;
};

const Top5SitesList = ({ title, sites, scoreKey, direction, scoreType, isLoading, slug = '', customerId }: Top5SitesListProps) => {
    return (
        <div className='bg-white shadow-portfolio-summary-card rounded-lg p-4 xl:p-5 flex flex-col gap-3'>
            <div className='flex items-center gap-2'>
                <span className='text-md text-primary'>{title}</span>
                <QuestionButtonHelp type='api' placement='top' slug={slug} icon={<Info className='h-4 w-4 text-primary' />} />
            </div>

            {isLoading ? (
                <div className='flex flex-col gap-2 animate-pulse'>
                    {Array.from({ length: 5 }).map((_, i) => (
                        <div key={i} className='flex items-center justify-between py-2'>
                            <div className='h-4 bg-grey-20 rounded w-1/2' />
                            <div className='h-4 bg-grey-20 rounded w-20' />
                        </div>
                    ))}
                </div>
            ) : sites.length === 0 ? (
                <div className='flex items-center justify-center h-24 text-sm text-muted-foreground'>No sites found</div>
            ) : (
                <div className='flex flex-col'>
                    <div className='flex items-center justify-between pb-1 px-2 '>
                        <span>Site</span>
                        <span>{scoreType} Score</span>
                    </div>
                    <div className='flex flex-col divide-y divide-grey-10'>
                        {sites.slice(0, 5).map(site => (
                            <div key={site.id} className='flex items-center justify-between py-3 px-2 gap-2'>
                                <SiteCell
                                    name={site.name}
                                    countryName={site.geography?.countryName ?? null}
                                    siteId={site.id}
                                    customerId={customerId}
                                />
                                <RiskScoreCell value={site.riskSummary?.[scoreKey] ?? null} direction={direction} layout='inline' />
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default Top5SitesList;
